import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";

import {
  listQuotations,
  QUOTATION_STATUS_LABEL,
  type Quotation,
} from "@/features/quotations/api";
import { formatAmount } from "@/lib/format";

/**
 * 案件のための見積として、**既にある見積を選ぶ**ダイアログ。
 *
 * 客先から後で見積依頼が届く（EDI 取り込み）ことがあり、その見積はもう画面の中に
 * ある。案件から起こし直すと同じ内容の見積が 2 本になるので、選べるようにする。
 *
 * 受注済みの見積は出さない。**既に別の案件を作っている**ので、この案件のものには
 * なり得ない（API も 409 で断る）。
 */
export default function QuotationPickerDialog({
  isOpen,
  clientId,
  isBusy,
  onClose,
  onSelect,
}: {
  isOpen: boolean;
  /** 案件の得意先。候補を同じ得意先に絞るのに使う（未設定なら絞らない）。 */
  clientId: number | null;
  isBusy: boolean;
  onClose: () => void;
  onSelect: (quotation: Quotation) => void;
}) {
  const [keyword, setKeyword] = useState("");

  // 開き直したら絞り込みは白紙に戻す（前に打った番号が残っていると、候補が
  // 0 件に見えて「選べる見積が無い」と読み違える）。
  useEffect(() => {
    if (isOpen) setKeyword("");
  }, [isOpen]);

  const quotations = useQuery({
    queryKey: ["quotations", "picker", clientId, keyword],
    queryFn: () =>
      listQuotations({
        keyword: keyword.trim() || undefined,
        clientId: clientId ?? undefined,
        limit: 50,
        offset: 0,
        sortKey: "quotation_number",
        ascending: false,
      }),
    enabled: isOpen,
  });

  // 受注済みは既に別の案件を作っているので候補にしない。
  const candidates = (quotations.data?.items ?? []).filter(
    (q) => q.status !== "ordered",
  );

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>この案件の見積を選ぶ</span>
          <span className="text-small font-normal text-default-500">
            客先から後で届いた見積をこの案件のものにします。金額を入れて受注にすると、案件と結び付きます。
          </span>
        </ModalHeader>

        <ModalBody>
          <Input
            size="sm"
            variant="bordered"
            radius="sm"
            aria-label="見積番号・得意先名で絞り込み"
            placeholder="見積番号・得意先名で絞り込み"
            value={keyword}
            isClearable
            startContent={<Search size={16} className="text-default-400" />}
            onValueChange={setKeyword}
            onClear={() => setKeyword("")}
          />

          {quotations.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner label="読み込み中..." />
            </div>
          ) : candidates.length === 0 ? (
            <p className="py-2 text-sm text-default-500">
              選べる見積がありません。受注済みの見積は既に別の案件を作っているため出てきません。
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-default-100">
              {candidates.map((quotation) => (
                <li
                  key={quotation.quotation_id}
                  className="flex items-center gap-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold">{quotation.quotation_number}</p>
                    <p className="truncate text-tiny text-default-400">
                      {quotation.client_name ?? "得意先なし"} / {quotation.item_count}{" "}
                      行 / {formatAmount(quotation.total_amount)} 円
                    </p>
                  </div>
                  <Chip size="sm" variant="flat">
                    {QUOTATION_STATUS_LABEL[quotation.status]}
                  </Chip>
                  <Button
                    size="sm"
                    radius="full"
                    color="primary"
                    variant="bordered"
                    // 同じ文言のボタンが並ぶので、読み上げにはどの見積かを乗せる。
                    aria-label={`${quotation.quotation_number} を選ぶ`}
                    isDisabled={isBusy}
                    onPress={() => onSelect(quotation)}
                  >
                    選ぶ
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </ModalBody>

        <ModalFooter>
          <Button radius="full" variant="light" data-testid="close" onPress={onClose}>
            閉じる
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
